import axios, { AxiosError, AxiosResponse } from "axios";
import { saveTokenResource, loadTokenResource } from "helpers/auth";
import { signOut as signOutCreator } from "reducers/auth";
import { store } from "./index";
import { endpoints, BASE_URL, HOST } from "config";
import { createUseTable } from "@avamae/table";
import { createUseFetch } from "helpers/useFetch";
import { addSeconds } from "date-fns";

const signOut = () => signOutCreator("Your session has expired. Please sign in again.", false);

const { refreshAccessToken: refreshEndpoint } = endpoints.auth;

/**
 * Custom axios instance with base url set, and some interceptors to
 * handle auth headers.
 *
 */
const instance = axios.create({
  baseURL: BASE_URL,
});

/**
 * A function that, given an access token, will modify an axios request
 * config and re-run the request.
 */
type RequestCallback = (token: string) => void;

/**
 * Some mutable state for a queue of requests. If we're already in the middle of
 * requesting a new token, we want to hold other requests in a queue, then make
 * them with the new token once we get it.
 */
class RequestHandler {
  isFetchingToken: boolean;
  private pendingRequests: RequestCallback[];

  constructor() {
    this.isFetchingToken = false;
    this.pendingRequests = [];
  }

  addToQueue = (callback: RequestCallback) => {
    this.pendingRequests.push(callback);
  };

  onTokenFetched = (token: string) => {
    this.pendingRequests.forEach((cb) => cb(token));
    this.clearQueue();
  };

  clearQueue = () => {
    this.pendingRequests = [];
    this.isFetchingToken = false;
  };
}

const reqHandler = new RequestHandler();

// Before each request, attach the stored token if it exists.
instance.interceptors.request.use((request) => {
  try {
    const resource = loadTokenResource();
    if (resource === null) throw new Error();
    const { accessToken } = resource;
    const authHeader = { Authorization: `Bearer ${accessToken}` };
    return { ...request, headers: { ...request.headers, ...authHeader } };
  } catch (err) {
    return request;
  }
});

/**
 * After each response, check if the response was a 401 Unauthorized
 * with a www-authenticate header indicating an expired or invalid token.
 * If so, attempt to refresh the access token and retry the request.
 * If the refresh fails, sign the user out.
 */
instance.interceptors.response.use(
  (fulfilled) => {
    return fulfilled;
  },
  (rejected: AxiosError) => {
    const status = rejected.response?.status;
    const headers = rejected.response?.headers;
    const wwwAuth = headers?.["www-authenticate"] || headers?.["Www-Authenticate"] || "";

    // Check for 401 and www-authenticate header indicating expired/invalid token
    if (
      status === 401 &&
      typeof wwwAuth === "string" &&
      wwwAuth.includes("invalid_token") &&
      wwwAuth.includes("expired")
    ) {
      // Refresh access token.
      return refreshAccessTokenOnError(rejected);
    }

    // It's not an authorisation issue, just pass on the rejection.
    return Promise.reject(rejected);
  }
);

type RefreshResponse = {
  details: {
    status: string;
    accessToken: string;
    tokenType: string;
    refreshTokenExpiresIn: string;
    expiresIn: string;
    refreshToken: string;
  };
};

/**
 * Intercept the error, and replace it with a promise (awaitingNewToken), which
 * on construction will add a callback to our RequestHandler's queue.
 *
 * The first time the request handler receives one of these additions to its
 * queue, it will attempt to fetch a new access token. It stores up pending
 * requests until the new token arrives. To the initial callers it looks like
 * their request is waiting for a response from the server.
 *
 * Once the request handler has fetched a new access token, it will run every
 * callback in its queue. The callback takes an original request's config,
 * changes it to use the new access token, and remakes the request. It then
 * resolves the promise made in awaitingNewToken. The response to this new
 * request is thereby delivered to the original caller. The queue is then cleared.
 *
 * To that initial caller, it just looks like its original request took a while
 * to resolve.
 */
const refreshAccessTokenOnError = async (rejected: AxiosError) => {
  console.log("Refreshing after endpoint error...");
  try {
    const { response: errorResponse } = rejected;

    // Extract the refresh token from LS and make sure it's there.
    // Otherwise, just return the error.

    const resource = loadTokenResource();
    if (resource == null) {
      console.log("Refresh failed (no tokens).");
      store.dispatch(signOut());
      return Promise.reject(rejected);
    }

    // Build a promise that will be handed to the caller instead of the error.
    const awaitingNewToken = new Promise((resolve) => {
      reqHandler.addToQueue((token) => {
        if (errorResponse && errorResponse.config.headers) {
          errorResponse.config.headers.Authorization = `Bearer ${token}`;
          resolve(axios(errorResponse.config));
        }
      });
    });

    // Check if we're already trying to fetch a replacement access token.
    if (!reqHandler.isFetchingToken) {
      reqHandler.isFetchingToken = true;
      const data = {
        accessToken: resource.accessToken,
        refreshToken: resource.refreshToken,
      };

      const response = await axios.post<
        typeof data,
        AxiosResponse<{ errors: any } & RefreshResponse>
      >(BASE_URL + refreshEndpoint, data); // NOTE: Add back when the real refresh endpoint is available
      // >(refreshEndpoint, data);

      if (!response.data || (response.data.errors && response.data.errors.length > 0)) {
        // The refresh request failed, reset the queue, sign the
        // user out and return the error.
        console.log("Refresh failed/rejected.");
        reqHandler.clearQueue();
        store.dispatch(signOut());
        return Promise.reject(rejected);
      }

      // The refresh request succeeded, save the token details for future
      // requests and make the queued requests again.
      console.log("Refresh succeeded.");

      const { accessToken, refreshToken, expiresIn, refreshTokenExpiresIn } = response.data.details;
      const tokenExpires = addSeconds(new Date(), parseInt(expiresIn, 10));
      const refreshTokenExpires = addSeconds(new Date(), parseInt(refreshTokenExpiresIn, 10));

      saveTokenResource({
        tokenExpires,
        accessToken,
        refreshToken,
        refreshTokenExpires,
      });
      reqHandler.onTokenFetched(accessToken);
    }

    // Return the promise, which will fulfill when we have a new token,
    // instead of the error.
    return awaitingNewToken;
  } catch (error) {
    // Something went generically wrong, return this error and signout.
    store.dispatch(signOut());
    return Promise.reject(error);
  }
};

/**
 * Attempt to refresh user access token with the access and refresh tokens
 * kept in LocalStorage. If no tokens stored or refresh fails (expired or
 * invalid refresh token) then log the user out.
 */
export const refreshAccessToken = async (onDone?: () => void) => {
  // Extract the refresh token from LS and make sure it's there.
  // Otherwise, reject promise.

  reqHandler.isFetchingToken = true;

  try {
    const resource = loadTokenResource();
    if (resource == null) {
      reqHandler.isFetchingToken = false;
      store.dispatch(signOutCreator("", false));
      return Promise.reject();
    }

    const data = {
      accessToken: resource.accessToken,
      refreshToken: resource.refreshToken,
      host: HOST,
    };

    const response = await axios.post<
      typeof data,
      AxiosResponse<{ errors: any } & RefreshResponse>
    >(BASE_URL + refreshEndpoint, data); // NOTE: Add back when the real refresh endpoint is available
    // >(refreshEndpoint, data);

    if (!response.data || (response.data.errors && response.data.errors.length > 0)) {
      // The refresh request failed sign the
      // user out and reject promise.
      reqHandler.isFetchingToken = false;
      store.dispatch(signOutCreator("", false));
      return Promise.reject();
    }

    // The refresh request succeeded, save the token details for future
    // requests.
    const { accessToken, refreshToken, expiresIn, refreshTokenExpiresIn } = response.data.details;
    const tokenExpires = addSeconds(new Date(), parseInt(expiresIn, 10));
    const refreshTokenExpires = addSeconds(new Date(), parseInt(refreshTokenExpiresIn, 10));

    saveTokenResource({
      tokenExpires,
      accessToken,
      refreshToken,
      refreshTokenExpires,
    });
    reqHandler.isFetchingToken = false;

    if (onDone) {
      onDone();
    }
  } catch (error) {
    console.log(error);
    reqHandler.isFetchingToken = false;
    store.dispatch(signOut());
    return Promise.reject();
  }
};

export default instance;
export const useTable = createUseTable(instance);
export type TableInfo = ReturnType<typeof useTable>;

export const useFetch = createUseFetch(instance);

export type AvamaeResponseStatus = "1" | "0";
export type AvamaeDefaultError = { fieldName: string; messageCode: string };
export type BaseAvamaeResponse<Details = any, Error = AvamaeDefaultError> = {
  details: Details;
  errors: Error[];
  id: number;
  status: AvamaeResponseStatus;
};

export type ErrorMessage = {
  type: any;
  fieldName: string;
  messageText: string;
};
